"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";
import { VideoData } from "@/lib/types";
import { Volume2, VolumeX } from "lucide-react";
import { motion } from "framer-motion";

// Register plugins to enable scroll-driven clip morphing
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, MorphSVGPlugin);
}

// --- Rounded clip shape -----------------------------------------------------
// CSS clip-path: polygon() only draws straight segments (and clip-path edges
// aren't anti-aliased in Chrome, so diagonals look jagged). Instead we mask the
// video with an SVG <mask> whose <path> has real quadratic-rounded corners —
// masks are alpha-blended, so the edges come out smooth — then morph that path on
// scroll. Coords are objectBoundingBox space (0..1) so the mask scales with it.
type Pt = [number, number];
// Per-corner rounding: only the slanted cut's own joints get a minimal curve —
// the real rectangle corners (r: 0) stay sharp, so the full-screen state is a
// plain rectangle exactly like before, not a rounded card.
type Corner = {
  p: Pt;
  r: number;
};
const fmt = (p: Pt) => `${p[0].toFixed(4)} ${p[1].toFixed(4)}`;

// Rounded corners get a quadratic curve (line to `r` before the vertex, curve
// through it to `r` after); sharp corners (r: 0) are a plain line to the vertex.
const roundedPath = (corners: Corner[]) => {
  const n = corners.length;
  const along = (from: Pt, to: Pt, r: number): Pt => {
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const len = Math.hypot(dx, dy);
    if (len === 0 || r === 0) return [from[0], from[1]];
    const d = Math.min(r, len / 2);
    return [from[0] + dx / len * d, from[1] + dy / len * d];
  };
  let out = "";
  for (let i = 0; i < n; i++) {
    const prev = corners[(i - 1 + n) % n].p;
    const {
      p: curr,
      r
    } = corners[i];
    const next = corners[(i + 1) % n].p;
    if (r === 0) {
      out += `${i === 0 ? "M" : "L"} ${fmt(curr)} `;
    } else {
      out += `${i === 0 ? "M" : "L"} ${fmt(along(curr, prev, r))} `;
      out += `Q ${fmt(curr)} ${fmt(along(curr, next, r))} `;
    }
  }
  return `${out}Z`;
};
const SLANT_R = 0.02; // minimal curve on the cut joints only

// Collapsed (slanted) shape → full-screen SHARP rectangle it opens into.
const CLIP_SLANT_D = roundedPath([{
  p: [0.46, 0.14],
  r: SLANT_R
},
// notch inner corner
{
  p: [0.5, 0.0],
  r: SLANT_R
},
// diagonal meets top edge
{
  p: [1.0, 0.0],
  r: 0
},
// top-right — sharp
{
  p: [1.0, 1.0],
  r: 0
},
// bottom-right — sharp
{
  p: [0.8, 1.0],
  r: 0
}, {
  p: [0.0, 1.0],
  r: 0
},
// bottom-left — sharp
{
  p: [0.0, 0.8],
  r: 0
}, {
  p: [0.0, 0.14],
  r: SLANT_R
} // horizontal meets left edge
]);
// Full-screen state uses the SAME 8-corner structure with each point mapped to a
// rectangle corner. Identical structure means MorphSVG moves every point in a
// straight line (no perimeter warp) — only the top-left notch opens; the rest of
// the video stays a static sharp rectangle the whole way.
const CLIP_FULL_D = roundedPath([{
  p: [0.0, 0.0],
  r: SLANT_R
},
// was notch inner
{
  p: [0.0, 0.0],
  r: SLANT_R
},
// was diagonal top
{
  p: [1.0, 0.0],
  r: 0
}, {
  p: [1.0, 1.0],
  r: 0
}, {
  p: [1.0, 1.0],
  r: 0
}, {
  p: [0.0, 1.0],
  r: 0
}, {
  p: [0.0, 1.0],
  r: 0
}, {
  p: [0.0, 0.0],
  r: SLANT_R
} // was horizontal/left join
]);
const MASK_ID = "scrollVideoMask";
interface ScrollVideoProps {
  data: VideoData;
  marqueeContent: React.ReactNode;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  onToggleAudio?: () => void;
  isMuted?: boolean;
  triggerRef?: React.RefObject<HTMLElement | null>;
  // "morph"  → SVG clip-path with rounded corners morphs the slanted cut open to
  //            a full-screen rounded rectangle (default). Video stays full-bleed.
  // "height" → width stays full the whole time, only the height opens up.
  variant?: "morph" | "height";
}
const ScrollExpandVideo = t0 => {
  const $ = _c(24);
  const {
    data,
    marqueeContent,
    videoRef,
    onToggleAudio,
    triggerRef,
    isMuted: t1,
    variant: t2
  } = t0;
  const isMuted = t1 === undefined ? true : t1;
  const variant = t2 === undefined ? "morph" : t2;
  const containerRef = useRef(null);
  const videoWrapperRef = useRef(null);
  const clipPathRef = useRef(null);
  const useMorph = variant !== "height";
  let t3;
  let t4;
  if ($[0] !== triggerRef || $[1] !== variant) {
    t3 = () => {
      const wrapper = videoWrapperRef.current;
      const container = containerRef.current;
      if (!wrapper || !container) {
        return;
      }
      const trigger = triggerRef?.current ?? container;
      const st = {
        trigger,
        start: "top 80%",
        end: "top 30%",
        scrub: 1
      } as const;
      let tween;
      if (variant === "height") {
        gsap.set(wrapper, {
          clipPath: "inset(32% 0% 32% 0% round 24px)",
          willChange: "clip-path"
        });
        tween = gsap.to(wrapper, {
          clipPath: "inset(0% 0% 0% 0% round 18px)",
          ease: "none",
          scrollTrigger: st
        });
      } else {
        const path = clipPathRef.current;
        if (!path) {
          return;
        }
        gsap.set(path, {
          attr: {
            d: CLIP_SLANT_D
          }
        });
        tween = gsap.to(path, {
          morphSVG: {
            shape: CLIP_FULL_D,
            shapeIndex: 0
          },
          ease: "none",
          scrollTrigger: st
        });
      }
      return () => {
        tween.scrollTrigger?.kill();
        tween.kill();
      };
    };
    t4 = [variant, triggerRef];
    $[0] = triggerRef;
    $[1] = variant;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== useMorph) {
    t5 = useMorph && <svg width="0" height="0" className="absolute" aria-hidden="true"><defs><mask id={MASK_ID} maskContentUnits="objectBoundingBox" maskUnits="objectBoundingBox"><path ref={clipPathRef} d={CLIP_SLANT_D} fill="#fff" /></mask></defs></svg>;
    $[4] = useMorph;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== marqueeContent) {
    t6 = <div className="absolute inset-0 z-0 flex items-start justify-start"><div className="w-[60%]">{marqueeContent}</div></div>;
    $[6] = marqueeContent;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== useMorph) {
    t7 = {
      backgroundColor: "#000",
      ...(useMorph ? {
        maskImage: `url(#${MASK_ID})`,
        WebkitMaskImage: `url(#${MASK_ID})`
      } : {
        clipPath: "inset(32% 0% 32% 0% round 24px)"
      })
    };
    $[8] = useMorph;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = data?.poster;
  let t9;
  if ($[10] !== t8 || $[11] !== videoRef) {
    t9 = <video ref={videoRef} poster={t8} preload="none" loop={true} muted={true} playsInline={true} className="w-full h-full object-cover" />;
    $[10] = t8;
    $[11] = videoRef;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== isMuted || $[14] !== onToggleAudio) {
    t10 = onToggleAudio && <button onClick={onToggleAudio} className="absolute bottom-8 right-8 z-30 group" aria-label={isMuted ? "Unmute video" : "Mute video"}><div className="relative w-28 h-28 xl:w-32 xl:h-32 flex items-center justify-center"><motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 12,
          repeat: Infinity,
          ease: "linear"
        }} className="absolute inset-0"><svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden="true"><path id="audioCircleDesktop" d="M 50, 50 m -38, 0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" fill="none" /><text className="text-[9px] uppercase font-black tracking-[0.25em] fill-primary"><textPath xlinkHref="#audioCircleDesktop">{isMuted ? "\u2022 LISTEN \u2022 UNMUTE \u2022 LISTEN \u2022 UNMUTE " : "\u2022 PLAYING \u2022 AUDIO \u2022 PLAYING \u2022 AUDIO "}</textPath></text></svg></motion.div><div className="relative z-10 w-14 h-14 xl:w-16 xl:h-16 rounded-full bg-primary flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">{isMuted ? <VolumeX className="w-6 h-6 xl:w-7 xl:h-7 text-accent-foreground" /> : <Volume2 className="w-6 h-6 xl:w-7 xl:h-7 text-accent-foreground" />}</div></div></button>;
    $[13] = isMuted;
    $[14] = onToggleAudio;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t7 || $[18] !== t9) {
    t11 = <div ref={videoWrapperRef} className="absolute inset-0 z-10 w-full h-full overflow-hidden" style={t7}>{t9}{t10}</div>;
    $[16] = t10;
    $[17] = t7;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t5 || $[22] !== t6) {
    t12 = <section ref={containerRef} className="relative w-full h-screen">{t5}{t6}{t11}</section>;
    $[20] = t11;
    $[21] = t5;
    $[22] = t6;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
export default ScrollExpandVideo;